import { Color } from '@persona-id/design-tokens';
import { ForwardedRef, forwardRef } from 'react';
import styled from 'styled-components';

import ImageIdBack from 'images/inquiry/scanner/id-back.svg';
import ImageIdBarcode from 'images/inquiry/scanner/id-barcode.svg';
import ImageIdFront from 'images/inquiry/scanner/id-front.svg';
import ImagePassportPortrait from 'images/inquiry/scanner/passport-portrait.svg';
import ImagePassportSignature from 'images/inquiry/scanner/passport-signature.svg';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import {
  COLLAPSE_ANIMATION_DURATION_SECONDS,
  FLIP_ANIMATION_DURATION_SECONDS,
  OVERLAY_HINT_VERTICAL_PADDING,
} from 'inquiry/components/ScannerOverlay/constants';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { colorCss } from 'lib/inquiryStyles';
import {
  SerializedStepGovernmentIdStyles,
  StepGovernmentIdConfig,
} from 'lib/interfaces/StepsConfig';
import { TextComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import ThemedSvg from 'verify/components/ThemedSvg';

interface CaptureFrameLabelProps {
  collapsed?: boolean;
  height?: number | string;

  styles: TextComponentStyles;
  borderRadius: string;

  assets: StepGovernmentIdConfig['assets']['capture-page'];
  side: GovernmentIdSide;
  idClass: GovernmentIdClass;

  text: string | null | undefined;
}

const CaptureFrameLabelFrame = styled.div<{
  height: number | string;
  borderRadius: string;
  collapsed?: boolean;
  styles?: TextComponentStyles;
}>`
  position: relative;
  align-items: flex-start;
  background: rgb(230, 230, 230);
  color: ${({ styles }) =>
    styles != null ? colorCss(styles?.['text-color'].base.base) : Color.CoreSlate800};
  padding: ${OVERLAY_HINT_VERTICAL_PADDING}px 12px;
  display: flex;
  /**
   * need to specify inner border radius to prevent overlapping into the parent border corner
   * when inside flipped mode, because we don't have overflow:hidden since it would break backface visbility for flip animation
   * border is necessary in all 4 corners on the hint when collapsed,
   * but only bottom left and bottom right corner when not collapsed
   * NOTE: Need two spaces between interpolation because Styled Components concatenates for some reason.
   */
  border-radius: ${({ borderRadius }) => borderRadius};
  height: ${({ height }) => height ?? 'auto'};
  width: 100%;
  /**
   * if transitioning to collapsed state, set border-radius to final state at end
   * when transitioning to uncollapsed state, set border-radius to final state at start
   */
  transition: ${({ collapsed }) => `height ${FLIP_ANIMATION_DURATION_SECONDS}s,
    border-radius ${COLLAPSE_ANIMATION_DURATION_SECONDS}s steps(1, ${
      collapsed ? 'jump-end' : 'jump-start'
    })`};
`;

const CaptureFrameLabelContents = styled.div`
  display: flex;
  align-items: center;

  font-size: 14px;
  line-height: 150%;

  img,
  svg {
    height: auto !important;
    width: 40px !important;
  }

  svg {
    path {
      mix-blend-mode: normal !important; // passport image has mix-blend-mode: darken which causes flip back-side to not work
    }
  }
`;

const CaptureFrameLabelText = styled('div')`
  align-self: center;
  margin-left: 12px;
`;

const PictographContainer = styled.span`
  flex: 1 0 auto;
  height: auto !important;
  width: 40px !important;
`;

const hidePictographContainer = (
  assets: StepGovernmentIdConfig['assets']['capture-page'],
  currentSide: GovernmentIdSide,
  governmentIdClass: GovernmentIdClass,
) => {
  let currentAsset;

  if (governmentIdClass === 'pp' || governmentIdClass === 'ipp') {
    if (currentSide === GovernmentIdSide.PassportSignature) {
      currentAsset = assets['passport-signature-pictograph'];
    } else {
      currentAsset = assets['passport-front-pictograph'];
    }
  } else {
    if (currentSide === GovernmentIdSide.Back) {
      currentAsset = assets['id-back-pictograph'];
    } else if (currentSide === GovernmentIdSide.BarcodePdf417) {
      currentAsset = assets['barcode-pdf417-pictograph'];
    } else if (
      currentSide === GovernmentIdSide.Front ||
      currentSide === GovernmentIdSide.FrontOrBack
    ) {
      currentAsset = assets['id-front-pictograph'];
    } else if (currentSide === GovernmentIdSide.PassportSignature) {
      currentAsset = assets['passport-signature-pictograph'];
    }
  }

  return (
    currentAsset?.styles?.width?.base?.base?.value === 0 ||
    currentAsset?.attributes?.width === '0px'
  );
};

const IdPictograph = ({
  assets,
  currentSide,
  governmentIdClass,
  styles,
}: {
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  currentSide: GovernmentIdSide;
  governmentIdClass: GovernmentIdClass;
  styles: SerializedStepGovernmentIdStyles['captureHintIcon'];
}) => {
  if (governmentIdClass === 'pp' || governmentIdClass === 'ipp') {
    switch (currentSide) {
      case GovernmentIdSide.PassportSignature:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-signature-pictograph']}
            alt='passport signature pictograph'
          >
            <ThemedSvg
              style={{ lineHeight: 0 }}
              $primaryColor={colorCss(styles?.fill.color)}
              $strokeColor={colorCss(styles?.stroke.color)}
              aria-hidden='true'
            >
              <ImagePassportSignature />
            </ThemedSvg>
          </CustomAssetWithFallback>
        );
      default:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-front-pictograph']}
            alt='passport front pictograph'
          >
            <ThemedSvg
              style={{ lineHeight: 0 }}
              $primaryColor={colorCss(styles?.fill.color)}
              $strokeColor={colorCss(styles?.stroke.color)}
              aria-hidden='true'
            >
              <ImagePassportPortrait />
            </ThemedSvg>
          </CustomAssetWithFallback>
        );
    }
  }

  switch (currentSide) {
    case GovernmentIdSide.Back:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-back-pictograph']}
          alt='ID back pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdBack style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.BarcodePdf417:
      return (
        <CustomAssetWithFallback
          customImage={assets['barcode-pdf417-pictograph']}
          alt='Barcode PDF417 pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdBarcode style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.Front:
    case GovernmentIdSide.FrontOrBack:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-front-pictograph']}
          alt='ID front pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdFront style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.PassportSignature:
      return (
        <CustomAssetWithFallback
          customImage={assets['passport-signature-pictograph']}
          alt='passport signature pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImagePassportSignature />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    default:
      return null;
  }
};

const CaptureFrameLabel = (props: CaptureFrameLabelProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { captureHintIcon: captureHintIconStyles } = useStepGovernmentIdStyleContext();

  const showPictographContainer = !hidePictographContainer(props.assets, props.side, props.idClass);

  return (
    <CaptureFrameLabelFrame
      collapsed={false}
      height={props.height ?? 'auto'}
      styles={props.styles}
      borderRadius={props.borderRadius}
    >
      <CaptureFrameLabelContents ref={ref}>
        {showPictographContainer && (
          <PictographContainer aria-hidden='true'>
            <IdPictograph
              assets={props.assets}
              currentSide={props.side}
              governmentIdClass={props.idClass}
              styles={captureHintIconStyles}
            />
          </PictographContainer>
        )}
        <CaptureFrameLabelText>{props.text}</CaptureFrameLabelText>
      </CaptureFrameLabelContents>
    </CaptureFrameLabelFrame>
  );
};

export default forwardRef(CaptureFrameLabel);
